import React, { useEffect, useState } from 'react';
import { useFinanceCheck } from '../FinanceCheckComponent/useFinanceCheckFaturas';
import { formatReal } from 'src/utils/format';
import { useConfiguration } from 'src/hooks/Configuration';
import { useToast } from 'src/hooks/Toast';
import api from 'src/services/api';
import handleFinanceReportPDF, { handleFinanceReportPDFWithSaque } from '../../handleFinanceReportPDF';
import downloadit from 'src/utils/download';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';

/* ─────────────────────────────────────────────
   Interfaces
───────────────────────────────────────────── */
interface SaleItem {
  _id: string;
  sale_hash: string;
  invoice_hash?: string;
  payer_id_response?: { name?: string };
  client_id_response?: { name?: string };
  total: number;
    installment_value: number | null;
  net_total: number;
  payment_method_id: string;
  payment_status_id: string;
  type?: string;
  createdAt?: string;
  approved_at?: string;
}

interface FinanceItem {
  item_id: number;
  hash: string;
  client: string;
  product: string;
  total: number | null;
   juros: number | null;
    multa: number | null;
  net_total: number;
  saldo: number;
  sacado: number;
  status: string;
  operation_type: string;
  operation_type_raw: string;
  payment_method_id: string;
  payment_method_id_reference?: string;
  fees: number | null;
  installment: number | null;
  installment_value: number | null;
  administration_tax: number | null;
  transaction_tax: number | null;
  credit_tax: number | null;
  sale_date?: string;
  tax: number | null;
  antecipation_gap: number;
  operation_fee: number;
  date: string;
  approved_at?: string;
  createdAt?: string;
  type?: string;
}



/* ─────────────────────────────────────────────
   Utilitários de data
───────────────────────────────────────────── */
const formatDate = (d?: string | null): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (d?: string | null): string => {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const addBusinessDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return result;
};

// Normaliza payment_method_id para as chaves canônicas usadas no sistema
const normalizePaymentMethod = (method?: string | null): string => {
  if (!method) return '';
  const m = method.toLowerCase().trim();
  if (m === 'pix') return 'pix';
  if (m === 'boleto') return 'boleto';
  if (m === 'credit_card' || m === 'cartão de crédito' || m === 'cartao de credito' || m === 'credit card' || m === 'creditcard') return 'credit_card';
  if (m === 'courtesy' || m === 'cortesia') return 'cortesia';
  if (m === 'gratuito' || m === 'free') return 'gratuito';
  return method;
};

const calcAvailableDate = (approvedAt?: string | null, paymentMethod?: string | null): Date | null => {
  if (!approvedAt || !paymentMethod) return null;
  const norm = normalizePaymentMethod(paymentMethod);
  const base = new Date(approvedAt);
  if (norm === 'credit_card') { const d = new Date(base); d.setDate(d.getDate() + 32); return d; }
  if (norm === 'boleto') return addBusinessDays(base, 3);
    if (norm === 'credit_card_recurring') { const d = new Date(base); d.setDate(d.getDate() + 32); return d; }
  if (norm === 'boleto_recurring') return addBusinessDays(base, 3);
  if (norm === 'pix')    return addBusinessDays(base, 1);
  return null;
};

const deadlineLabel: Record<string, string> = {
  credit_card: '32 dias corridos',
  boleto:      '3 dias úteis',
  pix:         '1 dia útil',
};

const getDeadlineLabel = (method?: string | null): string => {
  const norm = normalizePaymentMethod(method);
  return deadlineLabel[norm] ?? '—';
};

const paymentLabel: Record<string, string> = {
  credit_card: 'Cartão de Crédito',
  pix:         'PIX',
  boleto:      'Boleto',
  courtesy:    'Cortesia',
  cortesia:    'Cortesia',
  gratuito:    'Gratuito',
};

const getPaymentLabel = (method?: string | null): string => {
  const norm = normalizePaymentMethod(method);
  return paymentLabel[norm] ?? method ?? '—';
};

const statusMeta: Record<string, { label: string; color: string; bg: string }> = {
  paid:          { label: 'Pago',          color: '#14532d', bg: '#dcfce7' },
  cancelled:     { label: 'Cancelado',     color: '#7f1d1d', bg: '#fee2e2' },
  waiting:       { label: 'Aguardando',    color: '#78350f', bg: '#fef3c7' },
  available:     { label: 'Disponível',    color: '#1e3a5f', bg: '#dbeafe' },
  waiting_funds: { label: 'Aguard. Fundo', color: '#5b21b6', bg: '#ede9fe' },
};

/* ─────────────────────────────────────────────
   Componentes de UI
───────────────────────────────────────────── */
const Badge = ({ value }: { value: string }) => {
  const s = statusMeta[value] ?? { label: value, color: '#374151', bg: '#f3f4f6' };
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', background: s.bg, color: s.color, textTransform: 'uppercase' }}>
      {s.label}
    </span>
  );
};

const MoneyCell = ({ value, negative = false }: { value: number | null; negative?: boolean }) => {
  const v = value ?? 0;
  return <span style={{ fontVariantNumeric: 'tabular-nums', color: negative ? '#dc2626' : 'inherit' }}>{negative && v !== 0 ? '−' : ''}{formatReal(Math.abs(v))}</span>;
};

const SectionTitle = ({ children, count, accent = '#1e3a5f' }: { children: React.ReactNode; count?: number; accent?: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '28px 0 12px' }}>
    <div style={{ width: 4, height: 22, borderRadius: 2, background: accent, flexShrink: 0 }} />
    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1a2236' }}>{children}</h2>
    {count !== undefined && (
      <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, background: accent, color: '#fff', borderRadius: 999, padding: '2px 10px' }}>
        {count} item{count !== 1 ? 's' : ''}
      </span>
    )}
  </div>
);

const TableWrap = ({ children }: { children: React.ReactNode }) => (
  <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e5e9f0', marginBottom: 8 }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>{children}</table>
  </div>
);

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: string }) => (
  <th style={{ padding: '10px 14px', background: '#f1f5fb', color: '#6b7a99', fontWeight: 600, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: align as any, borderBottom: '1px solid #e5e9f0', whiteSpace: 'nowrap' }}>
    {children}
  </th>
);

const Td = ({ children, align = 'left', muted = false, noWrap = false, style: sx }: { children: React.ReactNode; align?: string; muted?: boolean; noWrap?: boolean; style?: React.CSSProperties }) => (
  <td style={{ padding: '9px 14px', borderBottom: '1px solid #f0f2f7', color: muted ? '#9aa0b0' : '#1a2236', textAlign: align as any, whiteSpace: noWrap ? 'nowrap' : undefined, ...sx }}>
    {children}
  </td>
);

const SummaryCard = ({ label, value, sub, accent, icon }: { label: string; value: string; sub?: string; accent: string; icon: string }) => (
  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e9f0', padding: '16px 20px', flex: '1 1 150px', minWidth: 140, boxShadow: '0 1px 4px rgba(30,58,95,0.06)', display: 'flex', flexDirection: 'column', gap: 4, borderTop: `3px solid ${accent}` }}>
    <div style={{ fontSize: 18 }}>{icon}</div>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#8693a8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 17, fontWeight: 800, color: '#1a2236', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#9aa0b0' }}>{sub}</div>}
  </div>
);

const AvailabilityCell = ({ approvedAt, paymentMethod, status }: { approvedAt?: string | null; paymentMethod?: string; status?: string }) => {
  if (status === 'available') return <span style={{ color: '#059669', fontWeight: 700, fontSize: 11 }}>Disponível</span>;
  const availDate = calcAvailableDate(approvedAt, paymentMethod);
  if (!availDate) return <span style={{ color: '#9aa0b0', fontSize: 11 }}>—</span>;
  const now = new Date();
  const isPast = availDate <= now;
  const diffDays = Math.ceil((availDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{ color: isPast ? '#dc2626' : '#6366f1', fontWeight: 700, fontSize: 11 }}>{formatDate(availDate.toISOString())}</span>
      <span style={{ fontSize: 10, color: isPast ? '#dc2626' : '#9aa0b0' }}>{isPast ? `Atrasado ${Math.abs(diffDays)}d` : `em ${diffDays}d`}</span>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Bloco de totais por método de pagamento
───────────────────────────────────────────── */
interface GroupedPayment { method: string; total: number; net_total: number; count: number }

const PaymentMethodTable = ({ title, items, accentColor, groupKey }: {
  title: string;
  items: any;
  accentColor: string;
  groupKey: string;
}) => {
  const methods = ['pix', 'credit_card', 'boleto'];
  const groups: GroupedPayment[] = methods.map(m => ({
    method: m,
    total:     items.filter(i => normalizePaymentMethod(i.payment_method_id) === m).reduce((a, i) => a + (i.installment_value ?? 0), 0),
    net_total: items.filter(i => normalizePaymentMethod(i.payment_method_id) === m).reduce((a, i) => a + (i.net_total ?? 0), 0),
    count:     items.filter(i => normalizePaymentMethod(i.payment_method_id) === m).length,
  })).filter(g => g.count > 0);

  const totalBruto  = groups.reduce((a, g) => a + g.total, 0);
  const totalLiq    = groups.reduce((a, g) => a + g.net_total, 0);
  const totalCount  = groups.reduce((a, g) => a + g.count, 0);

  if (totalCount === 0) return null;

  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: accentColor, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 3, height: 14, borderRadius: 2, background: accentColor }} />
        {title}
        <span style={{ fontWeight: 400, color: '#9aa0b0', textTransform: 'none' }}>({totalCount} venda{totalCount !== 1 ? 's' : ''})</span>
      </div>
      <TableWrap>
        <thead>
          <tr>
            <Th>Forma de Pagamento</Th>
            <Th align="right">Qtd</Th>
            <Th align="right">Bruto</Th>
            <Th align="right">Líquido</Th>
          </tr>
        </thead>
        <tbody>
          {groups.map(g => (
            <tr key={g.method} className="fin-tr">
              <Td>{paymentLabel[g.method] ?? g.method}</Td>
              <Td align="right" muted>{g.count}</Td>
              <Td align="right"><MoneyCell value={g.total} /></Td>
              <Td align="right"><strong><MoneyCell value={g.net_total} /></strong></Td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f1f5fb' }}>
            <td style={{ padding: '9px 14px', fontWeight: 700, color: '#1a2236', fontSize: 13 }}>Total</td>
            <td style={{ padding: '9px 14px', textAlign: 'right', color: '#9aa0b0' }}>{totalCount}</td>
            <td style={{ padding: '9px 14px', textAlign: 'right' }}><strong><MoneyCell value={totalBruto} /></strong></td>
            <td style={{ padding: '9px 14px', textAlign: 'right' }}><strong style={{ color: accentColor }}><MoneyCell value={totalLiq} /></strong></td>
          </tr>
        </tfoot>
      </TableWrap>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Componente Principal
───────────────────────────────────────────── */
const FinanceCheckComponentInvoice
 = () => {
  const { configuration, projectHash } = useConfiguration();
  const { loadGlobal, salesData, financeData, checkSalesNotFoundInSaldo, checkSalesNotPaidInSaldo, checkByOperation } = useFinanceCheck();
  const [ready, setReady] = useState(false);
  const [activeTab, setActiveTab] = useState<'resumo' | 'nao_conciliadas' | 'nao_pagas' | 'por_operacao' | 'linha_linha'>('resumo');
  const [expandedRows, setExpandedRows] = useState<Set<number | string>>(new Set());

  const {addToast} = useToast();

  const toggleRow = (id: number | string) => setExpandedRows(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

    const gerarRelatorioFinanceiroFaturasPDF = async () => {

    addToast({ title: 'Gerando relatório', type: 'info' });

    const projectItems = await api.get(`/projects`, { params: { where: { url : projectHash }}})

    const project =  projectItems?.data?.rows?.[0];

    const response = await api.get(`/check-saldo-static/${project.url}/faturas/SaleInvoice/json`);

    handleFinanceReportPDF({ data: response?.data, project: project })


  }


      const gerarRelatorioFinanceiroFaturasPDFSingle = async ({saque}) => {

    addToast({ title: 'Gerando relatório', type: 'info' });

    const projectItems = await api.get(`/projects`, { params: { where: { url : projectHash }}})

    const project =  projectItems?.data?.rows?.[0];

    const response = await api.get(`/check-saldo-static/${project.url}/faturas/SaleInvoice/json`);

    handleFinanceReportPDFWithSaque({ data: response?.data, project: project,saque })


  }

  const gerarRelatorioFinanceiro = async () => {

    addToast({ title: 'Gerando relatório', type: 'info' });

    await downloadit({ url: `/check-saldo-static/${projectHash}/faturas/SaleInvoice/xlsx` });


  }


  useEffect(() => {
    (async () => { await loadGlobal(projectHash); setReady(true); })();
  }, [projectHash]);

  if (!ready) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, flexDirection: 'column', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #e5e9f0', borderTopColor: '#1e3a5f', animation: 'spin 0.8s linear infinite' }} />
      <span style={{ color: '#6b7a99', fontSize: 14 }}>Carregando análise financeira…</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const sales   = (salesData   as SaleItem[])    ?? [];
  const finance = (financeData as FinanceItem[]) ?? [];
  const now     = new Date();
  

  // Saques realizados
  const saqueEntries  = finance.filter(f => f.type === 'transfer' || f.operation_type_raw === 'ted');
  const lastSaqueDate = saqueEntries.length > 0 ? Math.max(...saqueEntries.map(f => new Date(f.date).getTime())) : 0;

  // Créditos não sacados (pós último saque)
  const naoSacados = finance.filter(f =>
    f.type === 'payable' && f.sacado === 0 && f.operation_type_raw === 'credit' &&
    new Date(f.date).getTime() > lastSaqueDate
  );

  // Vendas que ainda não aparecem na conciliação
  const notFoundItems      = (checkSalesNotFoundInSaldo(salesData, financeData) ?? []) as SaleItem[];
  const notFoundAguardando = notFoundItems.filter(item => { const d = calcAvailableDate(item.approved_at, item.payment_method_id); return d !== null && d > now; });
  const notFoundNaoSacado  = notFoundItems.filter(item => { const d = calcAvailableDate(item.approved_at, item.payment_method_id); 
    
  
    
    return d === null || d <= now; });

  const notPaidItems = (checkSalesNotPaidInSaldo(salesData, financeData) ?? []) as FinanceItem[];
  const byOperation  = (checkByOperation(financeData) ?? []) as any[];

finance.filter(f => f.operation_type_raw === 'credit')?.map( i => {

if((i?.total || 0) < (i?.installment_value || 0)){
  i.total = (i?.installment_value || 0) +  (i?.juros || 0)  + (i?.multa || 0)
}

})

  // Totais
  const totalNaoSacado  = naoSacados.reduce((a, f) => a + f.net_total, 0);
  const totalTaxasTED   = saqueEntries.reduce((a, f) => a + (f.operation_fee ?? 0), 0);
  const totalJaSacado   = saqueEntries.reduce((a, f) => a + Math.abs(f.net_total), 0) + totalTaxasTED;
  const totalSaldo      = finance.length > 0 ? finance[finance.length - 1].saldo : 0;
  const totalArrecadado = finance.filter(f => f.operation_type_raw === 'credit').reduce((a, f) => a + (f.total ?? 0), 0);
  const totalLiquido    = finance.filter(f => f.operation_type_raw === 'credit').reduce((a, f) => a + f.net_total, 0);

  // Agregados para o DRE (apenas créditos)
  const creditEntries        = finance.filter(f => f.operation_type_raw === 'credit');
 
  const dreEntradaBruta      = creditEntries.reduce((a, f) => a + (f.total ?? 0), 0);
  const dreJurosParcelamento = creditEntries.reduce((a, f) => a + (f.fees ?? 0), 0);
  const dreTaxaVenda         = creditEntries.reduce((a, f) => a + (f.tax ?? 0), 0);
  const dreTaxaAdmin         = creditEntries.reduce((a, f) => a + (f.administration_tax ?? 0), 0);
  const dreTaxaCartao        = creditEntries.filter(f => normalizePaymentMethod(f.payment_method_id) === 'credit_card').reduce((a, f) => a + (f.transaction_tax ?? 0), 0);
  const dreGapAntecipacao    = creditEntries.reduce((a, f) => a + (f.antecipation_gap ?? 0), 0);
  const dreSaquesLiquido     = saqueEntries.reduce((a, f) => a + Math.abs(f.net_total), 0);
  const dreEntradaBrutaSemJuros = dreEntradaBruta - dreJurosParcelamento;

  // Chargebacks, estornos e refunds (todos os tipos que representam reversão de pagamento)
  const ESTORNO_TYPES_DEBITO  = ['chargeback', 'refund'];         // reduzem o saldo
  const ESTORNO_TYPES_CREDITO = ['chargeback_refund'];            // restauram o saldo
  const chargebackEntries      = finance.filter(f => ESTORNO_TYPES_DEBITO.includes(f.operation_type_raw ?? ''));
  const chargebackRefundEntries = finance.filter(f => ESTORNO_TYPES_CREDITO.includes(f.operation_type_raw ?? ''));
  const totalChargebacks       = chargebackEntries.reduce((a, f) => a + f.net_total, 0);
  const totalChargebackRefunds = chargebackRefundEntries.reduce((a, f) => a + f.net_total, 0);
  const totalEstornoLiquido    = totalChargebacks + totalChargebackRefunds;

  // Operações internas (bloqueios, adiantamentos — operation_type_raw === null)
  const internalEntries = finance.filter(f => f.operation_type_raw == null);

  // Vendas sacadas (presentes na conciliação e sacadas antes do lastSaqueDate)
  const sacadosItems = finance.filter(f =>
    f.type === 'payable' && f.operation_type_raw === 'credit' &&
    new Date(f.date).getTime() <= lastSaqueDate
  );
 
  // Linha a linha: mistura finance + vendas aguardando conciliação, ordenado por data
  // Entradas com operation_type_raw === null aparecem no extrato mas não afetam o saldo (se anulam entre si)

 

  const extratoRows: Array<{ kind: 'finance' | 'sale'; data: FinanceItem | SaleItem; sortDate: number }> = [
    ...finance.map(f => ({ kind: 'finance' as const, data: f, sortDate: new Date(f.date).getTime() })),
    ...notFoundAguardando.map(s => {
      const prevista = calcAvailableDate(s.approved_at, s.payment_method_id);
      const sortDate = prevista ? prevista.getTime() : new Date(s.approved_at ?? s.createdAt ?? '').getTime();
      return { kind: 'sale' as const, data: s, sortDate };
    }),
  ].sort((a, b) => a.sortDate - b.sortDate);

  const tabs: { id: typeof activeTab; label: string; badge?: number; alertColor?: string }[] = [
    { id: 'resumo',          label: 'Resumo' },
    { id: 'nao_conciliadas', label: 'Não Sacado', badge: notFoundItems.length, alertColor: notFoundNaoSacado.length > 0 ? '#dc2626' : notFoundAguardando.length > 0 ? '#d97706' : undefined },
    { id: 'nao_pagas',       label: 'Canceladas',  badge: notPaidItems.length,  alertColor: notPaidItems.length > 0 ? '#d97706' : undefined },
    { id: 'por_operacao',    label: 'Por Categoria' },
    { id: 'linha_linha',     label: 'Linha a Linha', badge: extratoRows.length },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',sans-serif", maxWidth: 1200, margin: '0 auto', padding: '0 4px' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .fin-tab { cursor:pointer; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; border:none; background:transparent; color:#6b7a99; transition:all 0.15s; }
        .fin-tab:hover { background:#f1f5fb; color:#1a2236; }
        .fin-tab.active { background:#1e3a5f; color:#fff; }
        .fin-tr:hover > td { background:#f7f9fd !important; }
      `}</style>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:'#1a2236', letterSpacing:'-0.02em' }}>Análise Financeira</h1>
          <p style={{ margin:'4px 0 0', color:'#8693a8', fontSize:13 }}>Conciliação e identificação de valores não sacados</p>
        </div>
        {naoSacados.length > 0 && (
          <div style={{ background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:10, padding:'10px 16px', display:'flex', alignItems:'center', gap:10 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'#92400e', textTransform:'uppercase', letterSpacing:'0.06em' }}>Valores não sacados</div>
              <div style={{ fontSize:18, fontWeight:800, color:'#78350f' }}>{formatReal(totalNaoSacado)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Cards */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:24 }}>
        <SummaryCard label="Total Arrecadado" value={formatReal(totalArrecadado)} sub="Bruto, apenas créditos"     accent="#1e3a5f" icon="💰" />
        <SummaryCard label="Total Líquido"    value={formatReal(totalLiquido)}    sub="Após taxas, sem estornos"    accent="#0369a1" icon="🏦" />
        {(chargebackEntries.length > 0 || chargebackRefundEntries.length > 0) && (
          <SummaryCard
            label="Estornos / Chargebacks"
            value={formatReal(Math.abs(totalEstornoLiquido))}
            sub={`${chargebackEntries.length} estorno${chargebackEntries.length !== 1 ? 's' : ''} e chargeback${chargebackEntries.length !== 1 ? 's' : ''}`}
            accent="#dc2626"
            icon="⚠️"
          />
        )}
        <SummaryCard label="Já Sacado"        value={formatReal(totalJaSacado)}   sub={`${saqueEntries.length} saque${saqueEntries.length !== 1 ? 's' : ''} · taxas TED: ${formatReal(totalTaxasTED)}`} accent="#6366f1" icon="✅" />
        <SummaryCard label="Saldo Atual"      value={formatReal(totalSaldo)}      sub="Após último saque"           accent="#059669" icon="📈" />
        <SummaryCard label="Não Sacado"       value={formatReal(totalNaoSacado)}  sub={`${naoSacados.length} transações pendentes`} accent="#d97706" icon="🔒" />
        <SummaryCard label="Não Conciliado"   value={String(notFoundItems.length)} sub={`${notFoundNaoSacado.length} atrasado${notFoundNaoSacado.length !== 1 ? 's' : ''}, ${notFoundAguardando.length} aguardando`} accent={notFoundNaoSacado.length > 0 ? '#dc2626' : notFoundAguardando.length > 0 ? '#d97706' : '#9aa0b0'} icon="🔍" />
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:20, background:'#f8fafc', borderRadius:12, padding:4, width:'fit-content' }}>
        {tabs.map(t => (
          <button key={t.id} className={`fin-tab${activeTab === t.id ? ' active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.label}
            {t.badge !== undefined && t.badge > 0 && (
              <span style={{ marginLeft:6, background: activeTab === t.id ? 'rgba(255,255,255,0.25)' : (t.alertColor ?? '#6b7a99'), color:'#fff', borderRadius:999, fontSize:10, fontWeight:800, padding:'1px 6px', display:'inline-block' }}>{t.badge}</span>
            )}
          </button>
        ))}

        <button   style={{ fontSize:11, color:'#6b7a99', border:'1px dashed #cbd5e1', borderRadius:8, padding:'6px 12px' }} className='find-tab active' onClick={() => gerarRelatorioFinanceiroFaturasPDF()}>PDF</button>
<button   style={{ fontSize:11, color:'#6b7a99', border:'1px dashed #cbd5e1', borderRadius:8, padding:'6px 12px' }} className='find-tab active' onClick={() => gerarRelatorioFinanceiro()}>Excel</button>
        <button
          className="fin-tab"
          onClick={() => {
            const tipos = [...finance.map(f => f.operation_type_raw)];
            const resumo = {
              operation_types_distintos: tipos,
              contagem_por_tipo: tipos.map(t => ({
                operation_type_raw: t,
                count: finance.filter(f => f.operation_type_raw === t).length,
                soma_net_total: finance.filter(f => f.operation_type_raw === t).reduce((a, f) => a + f.net_total, 0),
                soma_total: finance.filter(f => f.operation_type_raw === t).reduce((a, f) => a + (f.total ?? 0), 0),
              })),
              totais_conferencia: {
                saldo_final_api: totalSaldo,
                total_liquido_creditos: totalLiquido,
                total_estornos_liquido: totalEstornoLiquido,
                total_ja_sacado: totalJaSacado,
                diferenca_calculada: totalLiquido + totalEstornoLiquido - totalJaSacado,
              },
              todas_entradas: finance,
            };
            const blob = new Blob([JSON.stringify(resumo, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `finance-debug-${new Date().toISOString().slice(0,10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          style={{ fontSize:11, color:'#6b7a99', border:'1px dashed #cbd5e1', borderRadius:8, padding:'6px 12px' }}
        >
          🔍 Exportar debug
        </button>
      </div>

      {/* ══ TAB: Resumo ══ */}
      {activeTab === 'resumo' && (
        <div>

          {/* ══ DRE com validação de discrepância ══ */}
<SectionTitle accent="#1e3a5f">Demonstrativo de Resultado</SectionTitle>

{(() => {
  const cp1 = totalLiquido;   // net_total real somado da API — âncora 1
  const cp2 = totalSaldo;     // saldo final real da API      — âncora 2

  // ── Cálculo derivado (bottom-up, independente das âncoras) ──────────────
  const calc_liquidoAposTaxas =
    dreEntradaBruta
    - dreJurosParcelamento
    - dreTaxaVenda
    - dreTaxaAdmin
    - dreTaxaCartao
    - dreGapAntecipacao;

  const calc_saldoFinal =
    cp1                       // ancorado no líquido real
    - totalTaxasTED
    - dreSaquesLiquido
    + totalEstornoLiquido;    // estornos já são negativos → somamos o impacto líquido

  // ── Tolerância: diferenças de centavos por arredondamento são normais ───
  const TOLERANCE = 1; // centavo (os valores estão em centavos)

  const diff_cp1    = calc_liquidoAposTaxas - cp1;
  const diff_cp2    = calc_saldoFinal - cp2;
  const cp1_ok      = Math.abs(diff_cp1) <= TOLERANCE;
  const cp2_ok      = Math.abs(diff_cp2) <= TOLERANCE;
  const allOk       = cp1_ok && cp2_ok;

  const fmtSigned   = (v: number) => v < 0 ? `−${formatReal(Math.abs(v))}` : `+${formatReal(Math.abs(v))}`;
  const fmtAbs      = (v: number) => v < 0 ? `−${formatReal(Math.abs(v))}` : formatReal(v);

  // ── Componente de linha de diferença ────────────────────────────────────
  const DiffRow = ({ label, diff, ok }: { label: string; diff: number; ok: boolean }) => {
    if (ok) return null;
    return (
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 20px',
        background: '#fef2f2',
        borderLeft: '4px solid #dc2626',
        borderBottom: '1px solid #fecaca',
      }}>
        <span style={{ fontSize: 12, color: '#991b1b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          ⚠️ {label}
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
          {fmtSigned(diff)}
        </span>
      </div>
    );
  };

  type DreRow =
    | { kind: 'line'; label: string; lineValue: number; partial: number; sign: '+' | '-' | '+/-'; color?: string; muted?: boolean; indent?: boolean }
    | { kind: 'subtotal'; label: string; value: number; calcValue?: number; topBorder?: boolean }
    | { kind: 'divider' };

  const rows: DreRow[] = [
    { kind: 'line',     label: 'Entrada Bruta',                           sign: '+',   lineValue: dreEntradaBruta,          partial: dreEntradaBruta,                                                                         indent: false },
    { kind: 'line',     label: 'Juros de parcelamento',                   sign: '-',   lineValue: dreJurosParcelamento,      partial: dreEntradaBruta - dreJurosParcelamento,                                                   indent: true, color: '#dc2626' },
    { kind: 'subtotal', label: 'Entrada Bruta sem juros',                 value: dreEntradaBrutaSemJuros },
    { kind: 'divider' },
    { kind: 'line',     label: 'Taxa de venda (5%)',                      sign: '-',   lineValue: dreTaxaVenda,              partial: dreEntradaBrutaSemJuros - dreTaxaVenda,                                                   indent: true, color: '#dc2626' },
    { kind: 'line',     label: 'Taxa administrativa',                     sign: '-',   lineValue: dreTaxaAdmin,              partial: dreEntradaBrutaSemJuros - dreTaxaVenda - dreTaxaAdmin,                                    indent: true, color: '#dc2626' },
    { kind: 'line',     label: 'Taxa de cartão de crédito',               sign: '-',   lineValue: dreTaxaCartao,             partial: dreEntradaBrutaSemJuros - dreTaxaVenda - dreTaxaAdmin - dreTaxaCartao,                    indent: true, color: '#dc2626', muted: dreTaxaCartao === 0 },
    { kind: 'line',     label: 'Diferença de antecipação',                sign: '+/-', lineValue: -dreGapAntecipacao,        partial: dreEntradaBrutaSemJuros - dreTaxaVenda - dreTaxaAdmin - dreTaxaCartao - dreGapAntecipacao, indent: true, color: dreGapAntecipacao === 0 ? '#9aa0b0' : dreGapAntecipacao > 0 ? '#dc2626' : '#059669' },
    // CP1 — mostra tanto o calculado quanto o real; se divergirem o badge fica vermelho
    { kind: 'subtotal', label: 'Total Líquido (após taxas)',              value: cp1,  calcValue: calc_liquidoAposTaxas },
    { kind: 'divider' },
    { kind: 'line',     label: 'Taxa de saque (TED)',                     sign: '-',   lineValue: totalTaxasTED,             partial: cp1 - totalTaxasTED,                                                                      indent: true, color: '#dc2626' },
    { kind: 'line',     label: `Saques realizados (${saqueEntries.length}×)`, sign: '-', lineValue: dreSaquesLiquido,        partial: cp1 - totalTaxasTED - dreSaquesLiquido,                                                   indent: true, color: '#6366f1' },
    { kind: 'line',     label: `Estornos e chargebacks (${chargebackEntries.length})`, sign: '-', lineValue: Math.abs(totalEstornoLiquido), partial: cp1 - totalTaxasTED - dreSaquesLiquido + totalEstornoLiquido, indent: true, color: '#dc2626', muted: totalEstornoLiquido === 0 },
    { kind: 'divider' },
    // CP2 — saldo final real vs calculado
    { kind: 'subtotal', label: 'Saldo final',                             value: cp2,  calcValue: calc_saldoFinal, topBorder: true },
  ];

  return (
    <>
      {/* Banner de status geral */}
      {allOk ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
          <span style={{ fontSize: 18 }}>✅</span>
          <span style={{ fontWeight: 700, color: '#15803d' }}>DRE conciliado — todos os cálculos batem com os valores reais da API.</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <span style={{ fontWeight: 700, color: '#991b1b' }}>Discrepância detectada — o DRE calculado não bate com os valores reais.</span>
          </div>
          <div style={{ fontSize: 12, color: '#b91c1c', paddingLeft: 28 }}>
            Possíveis causas: taxas não mapeadas, estornos parciais, fields ausentes na API, ou arredondamento acima de {formatReal(TOLERANCE)}.
          </div>
          <div style={{ display: 'flex', gap: 24, paddingLeft: 28, marginTop: 4, flexWrap: 'wrap' }}>
            {!cp1_ok && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: '#9ca3af' }}>Diferença no Líquido: </span>
                <strong style={{ color: '#dc2626' }}>{fmtSigned(diff_cp1)}</strong>
                <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                  (calculado: {fmtAbs(calc_liquidoAposTaxas)} · real: {fmtAbs(cp1)})
                </span>
              </div>
            )}
            {!cp2_ok && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: '#9ca3af' }}>Diferença no Saldo: </span>
                <strong style={{ color: '#dc2626' }}>{fmtSigned(diff_cp2)}</strong>
                <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                  (calculado: {fmtAbs(calc_saldoFinal)} · real: {fmtAbs(cp2)})
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabela DRE */}
      <div style={{ background: '#fff', border: '1px solid #e5e9f0', borderRadius: 12, overflow: 'hidden', marginBottom: 28, maxWidth: 680 }}>
        {rows.map((row, i) => {
          if (row.kind === 'divider') return <div key={i} style={{ height: 1, background: '#f1f5fb' }} />;

          if (row.kind === 'subtotal') {
            const hasCalc    = row.calcValue !== undefined;
            const diffVal    = hasCalc ? row.calcValue! - row.value : 0;
            const matchesAPI = !hasCalc || Math.abs(diffVal) <= TOLERANCE;

            return (
              <React.Fragment key={i}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '11px 20px',
                  background: row.topBorder ? '#1e3a5f' : '#eef2f8',
                  borderTop: row.topBorder ? '2px solid #1e3a5f' : '2px solid #c8d3e8',
                  borderBottom: row.topBorder ? undefined : '2px solid #c8d3e8',
                }}>
                  <span style={{ fontWeight: 800, fontSize: 13, color: row.topBorder ? '#fff' : '#1a2236', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {!row.topBorder && <span style={{ fontSize: 11, color: '#6b7a99', fontWeight: 700 }}>(=)</span>}
                    {row.label}
                    {/* Badge de status do checkpoint */}
                    {hasCalc && (
                      matchesAPI
                        ? <span style={{ fontSize: 10, background: '#dcfce7', color: '#15803d', borderRadius: 999, padding: '2px 8px', fontWeight: 700 }}>✓ OK</span>
                        : <span style={{ fontSize: 10, background: '#fee2e2', color: '#dc2626', borderRadius: 999, padding: '2px 8px', fontWeight: 700 }}>✗ Diverge</span>
                    )}
                  </span>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    {/* Valor real (âncora) */}
                    <span style={{
                      fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums',
                      color: row.topBorder
                        ? (row.value >= 0 ? '#6ee7b7' : '#fca5a5')
                        : (row.value >= 0 ? '#059669' : '#dc2626'),
                    }}>
                      {fmtAbs(row.value)}
                    </span>
                    {/* Valor calculado — só mostra se diverge */}
                    {hasCalc && !matchesAPI && (
                      <span style={{ fontSize: 10, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                        calculado: {fmtAbs(row.calcValue!)}
                        <span style={{ marginLeft: 4, fontWeight: 700 }}>({fmtSigned(diffVal)})</span>
                      </span>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          }

          // Linhas normais
          const lv = row.lineValue;
          const displayStr = row.sign === '-'
            ? (lv !== 0 ? `−${formatReal(lv)}` : '—')
            : row.sign === '+/-'
              ? (lv === 0 ? '—' : (lv > 0 ? `+${formatReal(Math.abs(lv))}` : `−${formatReal(Math.abs(lv))}`))
              : formatReal(lv);

          return (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: '0 16px', padding: '9px 20px', paddingLeft: row.indent ? 36 : 20 }}>
              <span style={{ fontSize: 12.5, color: row.muted ? '#c0c7d4' : '#4a5568' }}>
                <span style={{ color: row.muted ? '#d1d5db' : '#9aa0b0', marginRight: 6, fontWeight: 700, fontSize: 11 }}>({row.sign})</span>
                {row.label}
              </span>
              <span style={{ fontWeight: 600, fontSize: 13, textAlign: 'right', minWidth: 110, fontVariantNumeric: 'tabular-nums', color: row.muted ? '#c0c7d4' : (row.color ?? '#1a2236') }}>
                {displayStr}
              </span>
              <span style={{ fontSize: 11, color: '#9aa0b0', fontVariantNumeric: 'tabular-nums', textAlign: 'right', minWidth: 110 }}>
                {fmtAbs(row.partial)}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
})()}

                    <SectionTitle accent="#d97706">Valores Pendentes de Saque</SectionTitle>
          {naoSacados.length === 0
            ? <p style={{ color:'#6b7a99', fontStyle:'italic' }}>Nenhum valor pendente de saque.</p>
            : (
              <TableWrap>
                <thead><tr>
                  <Th>Criação</Th><Th>Pagamento</Th><Th>Cliente</Th><Th>Produto</Th><Th>Método</Th>
                  <Th align="right">Bruto</Th><Th align="right">Taxa</Th><Th align="right">Líquido</Th>
                  <Th>Prazo</Th><Th>Disponível em</Th>
                </tr></thead>
                <tbody>
                  {naoSacados.map(item => (
                    <tr key={item.item_id} className="fin-tr">
                      <Td muted noWrap>{formatDate(item.date)}</Td>
                      <Td muted noWrap>{formatDateTime(item.approved_at)}</Td>
                      <Td><strong>{item.client}</strong></Td>
                      <Td muted>{item.product}</Td>
                      <Td>{getPaymentLabel(item.payment_method_id)}</Td>
                      <Td align="right"><MoneyCell value={item.total} /></Td>
                      <Td align="right"><MoneyCell value={item.tax} negative /></Td>
                      <Td align="right"><strong><MoneyCell value={item.net_total} /></strong></Td>
                      <Td muted>{getDeadlineLabel(item.payment_method_id)}</Td>
                      <Td><AvailabilityCell approvedAt={item.approved_at} paymentMethod={item.payment_method_id} status={item.status} /></Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#f1f5fb' }}>
                    <td colSpan={5} style={{ padding:'10px 14px', fontWeight:700, color:'#1a2236', fontSize:13 }}>Total pendente</td>
                    <Td align="right"><strong><MoneyCell value={naoSacados.reduce((a,f)=>a+(f.total??0),0)} /></strong></Td>
                    <Td align="right"><MoneyCell value={naoSacados.reduce((a,f)=>a+(f.tax??0),0)} negative /></Td>
                    <Td align="right"><strong style={{ color:'#059669' }}><MoneyCell value={totalNaoSacado} /></strong></Td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </TableWrap>
            )}

          {saqueEntries.length > 0 && (
            <>
              <SectionTitle accent="#059669">Saques Realizados</SectionTitle>
              <TableWrap>
                <thead><tr>
                  <Th>Data do Saque</Th><Th align="right">Valor Bruto</Th><Th align="right">Taxa TED</Th><Th align="right">Valor Líquido</Th><Th align="right">Saldo Após</Th>
                </tr></thead>
                <tbody>
                  {saqueEntries.map(item => (
                    <tr key={item.item_id} className="fin-tr">
                      <Td noWrap><strong>{formatDateTime(item.date)}</strong></Td>
                      <Td align="right"><MoneyCell value={Math.abs(item.total ?? 0)} /></Td>
                      <Td align="right">
                        <MoneyCell value={item.operation_fee} negative />
                        {item.operation_fee ? <div style={{ fontSize:10, color:'#9aa0b0', marginTop:1 }}>{formatReal(item.operation_fee)} TED</div> : null}
                      </Td>
                      <Td align="right"><strong style={{ color:'#059669' }}><MoneyCell value={Math.abs(item.net_total)} /></strong></Td>
                      <Td align="right" muted>{formatReal(item.saldo)}</Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#f1f5fb' }}>
                    <td style={{ padding:'9px 14px', fontWeight:700, color:'#1a2236' }}>Total</td>
                    <td style={{ padding:'9px 14px', textAlign:'right' }}><MoneyCell value={saqueEntries.reduce((a,f) => a + Math.abs(f.total ?? 0), 0)} /></td>
                    <td style={{ padding:'9px 14px', textAlign:'right' }}>
                      <span style={{ color:'#dc2626', fontWeight:600 }}>−{formatReal(totalTaxasTED)}</span>
                      <div style={{ fontSize:10, color:'#9aa0b0', marginTop:1 }}>{saqueEntries.length} × {formatReal(totalTaxasTED / saqueEntries.length)}</div>
                    </td>
                    <td style={{ padding:'9px 14px', textAlign:'right' }}><strong style={{ color:'#059669' }}><MoneyCell value={saqueEntries.reduce((a,f) => a + Math.abs(f.net_total), 0)} /></strong></td>
                    <td />
                  </tr>
                </tfoot>
              </TableWrap>
            </>
          )}

          {(chargebackEntries.length > 0 || chargebackRefundEntries.length > 0) && (
            <>
              <SectionTitle accent="#dc2626" count={chargebackEntries.length + chargebackRefundEntries.length}>Estornos e Chargebacks</SectionTitle>
              <TableWrap>
                <thead><tr>
                  <Th>Data</Th><Th>Tipo</Th><Th>Cliente</Th><Th>Produto</Th><Th align="right">Valor Líquido</Th><Th align="right">Saldo Após</Th>
                </tr></thead>
                <tbody>
                  {[...chargebackEntries, ...chargebackRefundEntries]
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map(item => {
                      const isReversao = ESTORNO_TYPES_CREDITO.includes(item.operation_type_raw ?? '');
                      return (
                        <tr key={item.item_id} className="fin-tr" style={{ background: isReversao ? '#fff7ed' : '#fff1f2', borderLeft: `3px solid ${isReversao ? '#f97316' : '#dc2626'}` }}>
                          <Td muted noWrap>{formatDateTime(item.date)}</Td>
                          <Td>
                            {isReversao
                              ? <span style={{ color:'#f97316', fontWeight:700 }}>↩️ {item.operation_type ?? 'Estorno Revertido'}</span>
                              : <span style={{ color:'#dc2626', fontWeight:700 }}>⚠️ {item.operation_type ?? 'Estorno/Chargeback'}</span>}
                          </Td>
                          <Td><strong>{item.client ?? '—'}</strong></Td>
                          <Td muted>{item.product ?? '—'}</Td>
                          <Td align="right">
                            <strong style={{ color: isReversao ? '#f97316' : '#dc2626' }}>
                              <MoneyCell value={item.net_total} negative={!isReversao} />
                            </strong>
                          </Td>
                          <Td align="right" muted>{formatReal(item.saldo)}</Td>
                        </tr>
                      );
                    })}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#f1f5fb' }}>
                    <td colSpan={4} style={{ padding:'10px 14px', fontWeight:700, color:'#1a2236', fontSize:13 }}>
                      Impacto líquido
                      <span style={{ marginLeft:8, fontSize:11, fontWeight:400, color:'#9aa0b0' }}>
                        ({chargebackEntries.length} estorno{chargebackEntries.length !== 1 ? 's' : ''}, {chargebackRefundEntries.length} revertido{chargebackRefundEntries.length !== 1 ? 's' : ''})
                      </span>
                    </td>
                    <td style={{ padding:'10px 14px', textAlign:'right' }}>
                      <strong style={{ color: totalEstornoLiquido >= 0 ? '#059669' : '#dc2626', fontSize:14 }}>
                        <MoneyCell value={totalEstornoLiquido} negative={totalEstornoLiquido < 0} />
                      </strong>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </TableWrap>
            </>
          )}
        </div>
      )}

      {/* ══ TAB: Não Sacado ══ */}
      {activeTab === 'nao_conciliadas' && (
        <div>
          {notFoundItems.length === 0 && <p style={{ color:'#059669', fontWeight:600, marginTop:8 }}>Todas as vendas estão conciliadas.</p>}

          {notFoundAguardando.length > 0 && (
            <>
              <SectionTitle accent="#d97706" count={notFoundAguardando.length}>Aguardando conciliação — prazo ainda não atingido</SectionTitle>
              <p style={{ fontSize:12, color:'#9aa0b0', marginTop:-10, marginBottom:12 }}>Estas vendas ainda estão dentro do prazo de compensação. Nenhuma ação necessária agora.</p>
              <TableWrap>
                <thead><tr>
                  <Th>Criação</Th><Th>Pagamento</Th><Th>Hash da Venda</Th><Th>Cliente</Th><Th>Método</Th><Th>Status</Th><Th>Prazo</Th><Th>Disponível em</Th><Th align="right">Total</Th>
                </tr></thead>
                <tbody>
                  {notFoundAguardando.map(item => (
                    <tr key={item._id} className="fin-tr" style={{ borderLeft:'3px solid #d97706' }}>
                      <Td muted noWrap>{formatDate(item.createdAt)}</Td>
                      <Td muted noWrap>{formatDateTime(item.approved_at)}</Td>
                      <Td muted><code style={{ fontSize:11, background:'#f1f5fb', padding:'2px 6px', borderRadius:4 }}>{item.invoice_hash}</code></Td>
                      <Td><strong>{item.payer_id_response?.name ?? item.client_id_response?.name ?? '—'}</strong></Td>
                      <Td>{getPaymentLabel(item.payment_method_id)}</Td>
                      <Td><Badge value={item.payment_status_id} /></Td>
                      <Td muted>{getDeadlineLabel(item.payment_method_id)}</Td>
                      <Td><AvailabilityCell approvedAt={item.approved_at} paymentMethod={item.payment_method_id} status={item.payment_status_id} /></Td>
                      <Td align="right"><MoneyCell value={item.installment_value} /></Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            </>
          )}

          {notFoundNaoSacado.length > 0 && (
            <>
              <SectionTitle accent="#dc2626" count={notFoundNaoSacado.length}>Não sacado — prazo de compensação já atingido</SectionTitle>
              <p style={{ fontSize:12, color:'#dc2626', marginTop:-10, marginBottom:12, fontWeight:600 }}>Estas vendas já deveriam estar disponíveis para saque. Verifique com a operadora.</p>
              <TableWrap>
                <thead><tr>
                  <Th>Criação</Th><Th>Pagamento</Th><Th>Hash da Venda</Th><Th>Cliente</Th><Th>Método</Th><Th>Status</Th><Th>Prazo</Th><Th>Venceu em</Th><Th align="right">Total</Th>
                </tr></thead>
                <tbody>
                  {notFoundNaoSacado.map(item => (
                    <tr key={item._id} className="fin-tr" style={{ background:'#fff5f5', borderLeft:'3px solid #dc2626' }}>
                      <Td muted noWrap>{formatDate(item.createdAt)}</Td>
                      <Td muted noWrap>{formatDateTime(item.approved_at)}</Td>
                      <Td muted><code style={{ fontSize:11, background:'#f1f5fb', padding:'2px 6px', borderRadius:4 }}>{item.invoice_hash}</code></Td>
                      <Td><strong>{item.payer_id_response?.name ?? item.client_id_response?.name ?? '—'}</strong></Td>
                      <Td>{getPaymentLabel(item.payment_method_id)}</Td>
                      <Td><Badge value={item.payment_status_id} /></Td>
                      <Td muted>{getDeadlineLabel(item.payment_method_id)}</Td>
                      <Td><AvailabilityCell approvedAt={item.approved_at} paymentMethod={item.payment_method_id} status={item.payment_status_id} /></Td>
                      <Td align="right"><strong style={{ color:'#dc2626' }}><MoneyCell value={item.installment_value} /></strong></Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#fff5f5' }}>
                    <td colSpan={8} style={{ padding:'10px 14px', fontWeight:700, color:'#dc2626', fontSize:13 }}>Total atrasado</td>
                    <Td align="right"><strong style={{ color:'#dc2626' }}><MoneyCell value={notFoundNaoSacado.reduce((a,i)=>a+(i.installment_value??0),0)} /></strong></Td>
                  </tr>
                </tfoot>
              </TableWrap>
            </>
          )}
        </div>
      )}

      {/* ══ TAB: Canceladas ══ */}
      {activeTab === 'nao_pagas' && (
        <div>
          <SectionTitle accent="#d97706" count={notPaidItems.length}>Vendas com status cancelado na conciliação</SectionTitle>
          {notPaidItems.length === 0
            ? <p style={{ color:'#059669', fontWeight:600 }}>Nenhuma venda com status cancelado na conciliação.</p>
            : (
              <TableWrap>
                <thead><tr>
                  <Th>Criação</Th><Th>Pagamento</Th><Th>Hash</Th><Th>Cliente</Th><Th>Produto</Th><Th>Método</Th><Th>Prazo</Th><Th>Disponível em</Th><Th align="right">Total</Th><Th align="right">Líquido</Th>
                </tr></thead>
                <tbody>
                  {notPaidItems.map(item => (
                    <tr key={item.item_id} className="fin-tr">
                      <Td muted noWrap>{formatDate(item.date)}</Td>
                      <Td muted noWrap>{formatDateTime(item.approved_at)}</Td>
                      <Td muted><code style={{ fontSize:11, background:'#f1f5fb', padding:'2px 6px', borderRadius:4 }}>{item.hash}</code></Td>
                      <Td><strong>{item.client}</strong></Td>
                      <Td muted>{item.product}</Td>
                      <Td>{getPaymentLabel(item.payment_method_id)}</Td>
                      <Td muted>{getDeadlineLabel(item.payment_method_id)}</Td>
                      <Td><AvailabilityCell approvedAt={item.approved_at} paymentMethod={item.payment_method_id} status={item.status} /></Td>
                      <Td align="right"><MoneyCell value={item.total} /></Td>
                      <Td align="right"><MoneyCell value={item.net_total} /></Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
        </div>
      )}

      {/* ══ TAB: Por Categoria ══ */}
      {activeTab === 'por_operacao' && (() => {
        const totalConciliadoBruto  = [...sacadosItems, ...naoSacados].reduce((a,f)=>a+(f.total??0),0);
        const totalConciliadoLiq    = [...sacadosItems, ...naoSacados].reduce((a,f)=>a+f.net_total,0);
        const totalAguardBruto      = notFoundAguardando.reduce((a,i)=>a+(i.total??0),0);
        const totalAguardLiq        = notFoundAguardando.reduce((a,i)=>a+(i.net_total??0),0);
        const totalGeralBruto       = totalConciliadoBruto + totalAguardBruto;
        const totalGeralLiq         = totalConciliadoLiq   + totalAguardLiq;

        return (
          <div>
            {/* ── Sacados ── */}
            <SectionTitle accent="#059669">Sacados</SectionTitle>
            <PaymentMethodTable title="Créditos já sacados" items={sacadosItems} accentColor="#059669" groupKey="sacados" />

            {/* ── Não Sacados ── */}
            <SectionTitle accent="#d97706">Não Sacados</SectionTitle>
            <PaymentMethodTable title="Créditos disponíveis, ainda não sacados" items={naoSacados} accentColor="#d97706" groupKey="naosacados" />

            {/* ── Aguardando Conciliação ── */}
            <SectionTitle accent="#6366f1">Aguardando Conciliação</SectionTitle>
            <PaymentMethodTable title="Vendas dentro do prazo de compensação" items={notFoundAguardando} accentColor="#6366f1" groupKey="aguardando" />

            {/* ── Totais Consolidados ── */}
            <SectionTitle accent="#1e3a5f">Totais Consolidados</SectionTitle>
            <TableWrap>
              <thead><tr>
                <Th>Segmento</Th><Th align="right">Bruto</Th><Th align="right">Líquido</Th>
              </tr></thead>
              <tbody>
                <tr className="fin-tr">
                  <Td><span style={{ color:'#059669', fontWeight:700 }}>✅ Total Conciliado</span></Td>
                  <Td align="right"><MoneyCell value={totalConciliadoBruto} /></Td>
                  <Td align="right"><strong style={{ color:'#059669' }}><MoneyCell value={totalConciliadoLiq} /></strong></Td>
                </tr>
                <tr className="fin-tr">
                  <Td><span style={{ color:'#6366f1', fontWeight:700 }}>🕐 Total Aguardando Conciliação</span></Td>
                  <Td align="right"><MoneyCell value={totalAguardBruto} /></Td>
                  <Td align="right"><strong style={{ color:'#6366f1' }}><MoneyCell value={totalAguardLiq} /></strong></Td>
                </tr>
              </tbody>
              <tfoot>
                <tr style={{ background:'#f1f5fb' }}>
                  <td style={{ padding:'10px 14px', fontWeight:800, color:'#1a2236', fontSize:14 }}>Total Geral</td>
                  <td style={{ padding:'10px 14px', textAlign:'right' }}><strong><MoneyCell value={totalGeralBruto} /></strong></td>
                  <td style={{ padding:'10px 14px', textAlign:'right' }}><strong style={{ color:'#1e3a5f', fontSize:15 }}><MoneyCell value={totalGeralLiq} /></strong></td>
                </tr>
              </tfoot>
            </TableWrap>

            {/* Detalhamento por forma de pagamento por segmento */}
            <SectionTitle accent="#1a2236">Detalhamento por Forma de Pagamento</SectionTitle>
            {(['pix','credit_card','boleto'] as const).map(method => {
              const sacM    = sacadosItems.filter(f => normalizePaymentMethod(f.payment_method_id) === method);
              const naoSacM = naoSacados.filter(f => normalizePaymentMethod(f.payment_method_id) === method);
              const aguardM = notFoundAguardando.filter(i => normalizePaymentMethod(i.payment_method_id) === method);
              if (sacM.length + naoSacM.length + aguardM.length === 0) return null;
              const concBruto = [...sacM,...naoSacM].reduce((a,f)=>a+(f.total??0),0);
              const concLiq   = [...sacM,...naoSacM].reduce((a,f)=>a+f.net_total,0);
              const aguBruto  = aguardM.reduce((a,i)=>a+(i.total??0),0);
              const aguLiq    = aguardM.reduce((a,i)=>a+(i.net_total??0),0);
              return (
                <div key={method} style={{ marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#1a2236', marginBottom:6, padding:'6px 0', borderBottom:'1px solid #e5e9f0' }}>
                    {paymentLabel[method]}
                  </div>
                  <TableWrap>
                    <thead><tr><Th>Situação</Th><Th align="right">Qtd</Th><Th align="right">Bruto</Th><Th align="right">Líquido</Th></tr></thead>
                    <tbody>
                      {sacM.length > 0 && <tr className="fin-tr" style={{ borderLeft:'3px solid #059669' }}>
                        <Td><span style={{ color:'#059669', fontWeight:600 }}>✅ Sacados</span></Td>
                        <Td align="right" muted>{sacM.length}</Td>
                        <Td align="right"><MoneyCell value={sacM.reduce((a,f)=>a+(f.total??0),0)} /></Td>
                        <Td align="right"><strong><MoneyCell value={sacM.reduce((a,f)=>a+f.net_total,0)} /></strong></Td>
                      </tr>}
                      {naoSacM.length > 0 && <tr className="fin-tr" style={{ borderLeft:'3px solid #d97706' }}>
                        <Td><span style={{ color:'#d97706', fontWeight:600 }}>🔒 Não Sacados</span></Td>
                        <Td align="right" muted>{naoSacM.length}</Td>
                        <Td align="right"><MoneyCell value={naoSacM.reduce((a,f)=>a+(f.total??0),0)} /></Td>
                        <Td align="right"><strong><MoneyCell value={naoSacM.reduce((a,f)=>a+f.net_total,0)} /></strong></Td>
                      </tr>}
                      {aguardM.length > 0 && <tr className="fin-tr" style={{ borderLeft:'3px solid #6366f1' }}>
                        <Td><span style={{ color:'#6366f1', fontWeight:600 }}>🕐 Aguardando Conciliação</span></Td>
                        <Td align="right" muted>{aguardM.length}</Td>
                        <Td align="right"><MoneyCell value={aguardM.reduce((a,i)=>a+(i.total??0),0)} /></Td>
                        <Td align="right"><strong><MoneyCell value={aguardM.reduce((a,i)=>a+(i.net_total??0),0)} /></strong></Td>
                      </tr>}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#f1f5fb' }}>
                        <td style={{ padding:'9px 14px', fontWeight:700, color:'#1a2236' }}>Total Conciliado</td>
                        <td style={{ padding:'9px 14px', textAlign:'right', color:'#9aa0b0' }}>{sacM.length + naoSacM.length}</td>
                        <td style={{ padding:'9px 14px', textAlign:'right' }}><strong><MoneyCell value={concBruto} /></strong></td>
                        <td style={{ padding:'9px 14px', textAlign:'right' }}><strong style={{ color:'#059669' }}><MoneyCell value={concLiq} /></strong></td>
                      </tr>
                      <tr style={{ background:'#f5f3ff' }}>
                        <td style={{ padding:'9px 14px', fontWeight:700, color:'#6366f1' }}>Total Aguardando Conciliação</td>
                        <td style={{ padding:'9px 14px', textAlign:'right', color:'#9aa0b0' }}>{aguardM.length}</td>
                        <td style={{ padding:'9px 14px', textAlign:'right' }}><strong><MoneyCell value={aguBruto} /></strong></td>
                        <td style={{ padding:'9px 14px', textAlign:'right' }}><strong style={{ color:'#6366f1' }}><MoneyCell value={aguLiq} /></strong></td>
                      </tr>
                    </tfoot>
                  </TableWrap>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ══ TAB: Linha a Linha ══ */}
      {activeTab === 'linha_linha' && (
        <div>
          <SectionTitle accent="#6366f1" count={extratoRows.length}>Extrato linha a linha</SectionTitle>
          <div style={{ display:'flex', gap:16, marginBottom:12, fontSize:11, flexWrap:'wrap' }}>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:'#dcfce7', border:'1.5px solid #059669', display:'inline-block' }} /> Saque (TED)</span>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:'#fffbeb', border:'1.5px solid #d97706', display:'inline-block' }} /> Não sacado (crédito conciliado pendente)</span>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:'#f5f3ff', border:'1.5px solid #6366f1', display:'inline-block' }} /> Aguardando conciliação (venda ainda no prazo)</span>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:'#fff1f2', border:'1.5px solid #dc2626', display:'inline-block' }} /> Chargeback / Estorno</span>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:'#fff7ed', border:'1.5px solid #f97316', display:'inline-block' }} /> Estorno revertido</span>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:'#f8fafc', border:'1.5px solid #cbd5e1', display:'inline-block' }} /> Op. interna (bloqueio/adiantamento)</span>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:'#f7f9fd', border:'1.5px solid #e5e9f0', display:'inline-block' }} /> Coberto por saque anterior</span>
          </div>
          <TableWrap>
            <thead><tr>
              <Th>Pagamento</Th><Th>Conciliação</Th><Th>Operação</Th><Th>Cliente</Th><Th>Produto</Th><Th>Método</Th>
              <Th align="right">Bruto</Th><Th align="right">Líquido</Th><Th align="right">Saldo Acum.</Th>
              <Th>Disponível em</Th><Th>Situação</Th>
            </tr></thead>
            <tbody>
              {(() => {
                // Pré-calcula o saldo acumulado para cada linha na ordem de exibição.
                // Finance rows já trazem o saldo real; sale rows acumulam sobre o último saldo conhecido.
                let runningSaldo = 0;
                const rowsWithSaldo = extratoRows.map(row => {
                  if (row.kind === 'finance') {
                    const f = row.data as FinanceItem;
                    const isInternal = f.operation_type_raw == null;
                    // Operações internas (bloqueios, adiantamentos) não alteram o saldo acumulado — se anulam entre si
                    if (!isInternal) runningSaldo = f.saldo ?? runningSaldo;
                    return { ...row, saldoExibido: runningSaldo, isProjected: false };
                  } else {
                    runningSaldo += row?.data?.payment_method_id === 'credit_card_recurring' ?((row?.data.installment_value || 0) * 0.95) +99 : (row?.data.installment_value || 0) * 0.95 ; 
                    return { ...row, saldoExibido: runningSaldo, isProjected: true };
                  }
                });
     let saqueIndex = 0;
                return rowsWithSaldo.map((row) => {

             


                  if (row.kind === 'finance') {
                    const item        = row.data as FinanceItem;
                    const isSaque     = item.type === 'ted' || item.type === 'transfer' || item.operation_type_raw === 'ted';
                    const isChargeback = ESTORNO_TYPES_DEBITO.includes(item.operation_type_raw ?? '');
                    const isChargebackRefund = ESTORNO_TYPES_CREDITO.includes(item.operation_type_raw ?? '');
                    const isInternal  = item.operation_type_raw == null;
                    const isNaoSacado = !isSaque && !isChargeback && !isChargebackRefund && !isInternal && item.sacado === 0 && item.operation_type_raw === 'credit' && new Date(item.date).getTime() > lastSaqueDate;
                    const rowBg     = isSaque ? '#f0fdf4' : isChargeback ? '#fff1f2' : isChargebackRefund ? '#fff7ed' : isInternal ? '#f8fafc' : isNaoSacado ? '#fffbeb' : 'transparent';
                    const rowBorder = isSaque ? '3px solid #059669' : isChargeback ? '3px solid #dc2626' : isChargebackRefund ? '3px solid #f97316' : isInternal ? '3px solid #cbd5e1' : isNaoSacado ? '3px solid #d97706' : '3px solid transparent';
         
                    const operacaoCell = isSaque
                      ? <span style={{ color:'#059669', fontWeight:700 }}>Saque (TED)</span>
                      : isChargeback
                        ? <span style={{ color:'#dc2626', fontWeight:700 }}>⚠️ {item.operation_type ?? 'Estorno/Chargeback'}</span>
                        : isChargebackRefund
                          ? <span style={{ color:'#f97316', fontWeight:700 }}>↩️ {item.operation_type ?? 'Estorno Revertido'}</span>
                          : isInternal
                            ? <span style={{ color:'#64748b', fontStyle:'italic' }}>🔧 {item.operation_type ?? 'Op. Interna'}</span>
                            : <span style={{ color:'#0369a1' }}>{item.operation_type}</span>;
if (isSaque) {
  saqueIndex += 1;
}

 
const currentSaqueIndex = saqueIndex;


                    const situacaoCell = isSaque
                      ? <span style={{ color:'#059669', fontWeight:700, fontSize:11 }}>✅ Sacado</span>
                      : isChargeback
                        ? <span style={{ color:'#dc2626', fontWeight:700, fontSize:11 }}>⚠️ Estorno</span>
                        : isChargebackRefund
                          ? <span style={{ color:'#f97316', fontWeight:700, fontSize:11 }}>↩️ Revertido</span>
                          : isInternal
                            ? <span style={{ color:'#64748b', fontSize:11, fontStyle:'italic' }}>Aj. Adm.</span>
                            : isNaoSacado
                              ? <span style={{ color:'#d97706', fontWeight:700, fontSize:11 }}>🔒 Não sacado</span>
                              : <span style={{ color:'#9aa0b0', fontSize:11 }}>Coberto</span>;
                    const isCredit = item.operation_type_raw === 'credit';
                    const isExpanded = expandedRows.has(item.item_id);
                    const isCreditCard = normalizePaymentMethod(item.payment_method_id) === 'credit_card';

                    // Cálculo do detalhamento financeiro (apenas para créditos)
                    // a = Bruto − juros − taxa venda − taxa cartão − taxa admin (líquido esperado)
                    // b = net_total (líquido recebido)
                    // gap = b − a  (positivo = recebeu mais que esperado; negativo = recebeu menos)
                    const brutoSemJuros   = (item.total ?? 0) - (item.fees ?? 0);
                    const taxaVenda       = item.tax ?? 0;
                    const taxaAdmin       = item.administration_tax ?? 0;
                    const adicionalCartao = isCreditCard ? (item.transaction_tax ?? 0) : 0;
                    const somaDeducoes    = (item.fees ?? 0) + taxaVenda + taxaAdmin + adicionalCartao;
                    const a               = (item.total ?? 0) - somaDeducoes;   // líquido esperado
                    const b               = item.net_total;                      // líquido recebido
                    const gap             = b - a;                               // gap = b − a
                    const liquidoEsperado = a;


                 
                    return (
                      <React.Fragment key={`f-${item.item_id}`}>
                        <tr
                          className="fin-tr"
                          style={{ background:rowBg, borderLeft:rowBorder, cursor: isCredit ? 'pointer' : 'default' }}
                          onClick={() => isCredit && toggleRow(item.item_id)}
                        >
                            <Td muted noWrap>{formatDateTime(item.approved_at)}</Td>
                          <Td muted noWrap>{formatDate(item.date)}</Td>
                        
                          <Td>
                            {operacaoCell}
                            {isCredit && item.installment && item.installment > 1 && (
                              <span style={{ marginLeft:6, fontSize:10, background:'#e0e7ff', color:'#3730a3', borderRadius:4, padding:'1px 5px', fontWeight:700 }}>
                                {item.installment}×
                              </span>
                            )}
                          </Td>
                          <Td><strong>{item.client ?? '—'}</strong></Td>
                          <Td muted>{item.product ?? '—'}</Td>
                          <Td muted>{getPaymentLabel(item.payment_method_id)}</Td>
                          <Td align="right"><MoneyCell value={item.total} negative={isSaque || isChargeback} /></Td>
                          <Td align="right"><strong><MoneyCell value={item.net_total} negative={isSaque || isChargeback} /></strong></Td>
                          <Td align="right"><strong>{formatReal(row.saldoExibido)}</strong></Td>
                          <Td>{(isSaque || isChargeback || isChargebackRefund || isInternal) ? <span style={{ color:'#9aa0b0', fontSize:11 }}>—</span> : <AvailabilityCell approvedAt={item.approved_at} paymentMethod={item.payment_method_id} status={item.status} />}</Td>
                          <Td>
                            {situacaoCell}

{isSaque ?  <FaFilePdf onClick={() => gerarRelatorioFinanceiroFaturasPDFSingle({saque : currentSaqueIndex})} size={20} style={{cursor: 'pointer'}}/> : <></>}


                            {isCredit && <span style={{ marginLeft:6, fontSize:10, color:'#9aa0b0' }}>{isExpanded ? '▲' : '▼'}</span>}
                          </Td>
                        </tr>
                        {isCredit && isExpanded && (
                          <tr key={`f-detail-${item.item_id}`}>
                            <td colSpan={11} style={{ padding:'0 14px 12px 28px', background: rowBg === 'transparent' ? '#fafbfd' : rowBg }}>
                              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:'8px 16px', padding:'10px 14px', background:'#fff', borderRadius:8, border:'1px solid #e5e9f0', fontSize:12 }}>

                                {/* Parcelas */}
                                {item.installment && item.installment > 1 && (
                                  <div>
                                    <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Parcelas</div>
                                    <div style={{ fontWeight:700, color:'#3730a3' }}>{item.installment}× de {formatReal(item.installment_value)}</div>
                                  </div>
                                )}

                                {/* Bruto total */}
                                <div>
                                  <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Bruto total</div>
                                  <div style={{ fontWeight:700 }}>{formatReal(item.total)}</div>
                                </div>

                                {/* Juros de parcelamento */}
                                {(item.fees ?? 0) > 0 && (
                                  <div>
                                    <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Juros de parcelamento</div>
                                    <div style={{ color:'#dc2626', fontWeight:600 }}>−{formatReal(item.fees)}</div>
                                  </div>
                                )}

                                {/* Bruto sem juros */}
                                {(item.fees ?? 0) > 0 && (
                                  <div>
                                    <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Bruto sem juros</div>
                                    <div style={{ fontWeight:600 }}>{formatReal(brutoSemJuros)}</div>
                                  </div>
                                )}

                                {/* Taxa de venda */}
                                <div>
                                  <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Taxa de venda (5%)</div>
                                  <div style={{ color:'#dc2626', fontWeight:600 }}>−{formatReal(taxaVenda)}</div>
                                </div>

                                {/* Taxa administrativa */}
                                <div>
                                  <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Taxa administrativa</div>
                                  <div style={{ color:'#dc2626', fontWeight:600 }}>−{formatReal(taxaAdmin)}</div>
                                </div>

                                {/* Taxa de cartão */}
                                {isCreditCard && (
                                  <div>
                                    <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Taxa de cartão</div>
                                    <div style={{ color:'#dc2626', fontWeight:600 }}>−{formatReal(adicionalCartao)}</div>
                                  </div>
                                )}

                                {/* Líquido esperado (a) */}
                                <div>
                                  <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                                    (a) Líquido esperado
                                  </div>
                                  <div style={{ fontWeight:700, fontFamily:'monospace' }}>{formatReal(liquidoEsperado)}</div>
                                  <div style={{ fontSize:9, color:'#c0c7d4', marginTop:1 }}>bruto − juros − taxas</div>
                                </div>

                                {/* Líquido recebido (b) */}
                                <div>
                                  <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                                    (b) Líquido recebido
                                  </div>
                                  <div style={{ fontWeight:800, color:'#059669', fontFamily:'monospace' }}>{formatReal(b)}</div>
                                  <div style={{ fontSize:9, color:'#c0c7d4', marginTop:1 }}>net_total</div>
                                </div>

                                {/* gap = b − a */}
                                <div style={{ gridColumn: 'span 2' }}>
                                  <div style={{ color:'#9aa0b0', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                                    gap = b − a
                                  </div>
                                  <div style={{ fontWeight:700, fontFamily:'monospace', color: gap === 0 ? '#9aa0b0' : gap > 0 ? '#059669' : '#dc2626' }}>
                                    {gap > 0 ? '+' : ''}{formatReal(gap)}
                                    {gap !== 0 && (
                                      <span style={{ fontSize:10, fontWeight:400, marginLeft:6, fontFamily:'sans-serif' }}>
                                        {gap > 0 ? '— recebeu mais que o esperado (crédito ao cliente)' : '— recebeu menos que o esperado (cobrado a mais)'}
                                      </span>
                                    )}
                                  </div>
                                </div>

                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  } else {
                    // Linha de venda aguardando conciliação — saldo projetado
                    const item = row.data as SaleItem;
                    const cliente = item.payer_id_response?.name ?? item.client_id_response?.name ?? '—';
                    return (
                      <tr key={`s-${item._id}`} className="fin-tr" style={{ background:'#f5f3ff', borderLeft:'3px solid #6366f1' }}>
                        <Td muted noWrap>{formatDate(item.createdAt)}</Td>
                        <Td muted noWrap>{formatDateTime(item.approved_at)}</Td>
                        <Td><span style={{ color:'#6366f1', fontWeight:700 }}>🕐 Ag. Conciliação</span></Td>
                        <Td><strong>{cliente}</strong></Td>
                        <Td muted>—</Td>
                        <Td muted>{getPaymentLabel(item.payment_method_id)}</Td>
                        <Td align="right"><MoneyCell value={item.installment_value} /></Td>
                        <Td align="right"><MoneyCell value={ item?.payment_method_id === 'credit_card_recurring' ?((item.installment_value || 0) * 0.95) +99 : (item.installment_value || 0) * 0.95} /></Td>
                        <Td align="right">
                          <span style={{ color:'#6366f1', fontWeight:700 }}>{formatReal(row.saldoExibido)}</span>
                          <div style={{ fontSize:9, color:'#a5b4fc', marginTop:1 }}>projetado</div>
                        </Td>
                        <Td><AvailabilityCell approvedAt={item.approved_at} paymentMethod={item.payment_method_id} status={item.payment_status_id} /></Td>
                        <Td><span style={{ color:'#6366f1', fontWeight:700, fontSize:11 }}>🕐 Aguardando</span></Td>
                      </tr>
                    );
                  }
                });
              })()}
            </tbody>
          </TableWrap>
        </div>
      )}
    </div>
  );
};

export default FinanceCheckComponentInvoice
;
